/**
 * Labstep
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGridCellEditorSearchSelect } from 'labstep-web/core/DataGrid/CellEditor/SearchSelect';
import {
  suppressKeyboardEventClearCells,
  suppressKeyboardEventDisableEnterOnEdit,
} from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import { TextLink } from 'labstep-web/core/DataGrid/Text/Link';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import { setValue } from 'labstep-web/core/DataGrid/utils';
import colDefEntityLink from 'labstep-web/grid/Index/coldefs/Resource/entityLink';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import type { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import type { Folder } from 'labstep-web/models/folder.model';
import type { Metadata } from 'labstep-web/models/metadata';
import type { OrderRequest } from 'labstep-web/models/order-request.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import {
  PurchaseOrder,
  PurchaseOrderStatus,
} from 'labstep-web/models/purchase-order.model';
import type { Resource } from 'labstep-web/models/resource.model';

const colDefEntityLinkPurchaseOrder = colDefEntityLink<OrderRequest>({
  getNestedEntity: (params) => params.data?.purchase_order,
  placeholder: 'Select',
});

export const colDefOrderRequestPurchaseOrder: NewIndexColDef = {
  colId: 'order',
  headerComponentParams: {
    icon: ICONS.purchase_order.primary,
  },
  headerName: PurchaseOrder.getHumanReadableEntityName(false, true),
  suppressKeyboardEvent: (params) =>
    suppressKeyboardEventDisableEnterOnEdit(params) ||
    suppressKeyboardEventClearCells(params),
  editable: (params: EditableCallbackParams) =>
    GridNewIndexService.getEditable(params),
  valueGetter: (params: ValueGetterParams) =>
    params.data?.purchase_order?.idAttr || '',
  valueSetter: (params: ValueSetterParams) =>
    setValue('purchase_order_id', params.newValue, {
      entityName: params.data?.entityName,
      id: params.data?.id,
    }),
  cellEditorPopup: true,
  cellEditor: (
    params: CellEditorParams<
      | Resource
      | Folder
      | ExperimentWorkflow
      | ProtocolValue
      | Metadata
    >,
  ) => (
    <DataGridCellEditorSearchSelect
      {...params}
      props={{
        entity: params.data,
        entityName: PurchaseOrder.entityName,
        params: {
          status: PurchaseOrderStatus.open,
        },
        getBody: (value) => ({
          purchase_order_id: value?.id || null,
        }),
      }}
    />
  ),
  cellRenderer: (params: CellRendererParams<Resource>) => {
    if (!params.data.purchase_order) {
      return (
        <DataGridPlaceholder
          params={params}
          editable={GridNewIndexService.getEditable(params)}
          placeholder="Enter"
        />
      );
    }
    return <TextLink entity={params.data?.purchase_order} />;
  },
};
